import { z } from 'zod';
export const SDK_VERSION = '1.0.0';
export const MAX_BUNDLE_BYTES = 8 * 1024 * 1024;
const id = z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/u);
const version = z.string().regex(/^1\.\d+\.\d+$/u);
const assetPath = z
  .string()
  .max(200)
  .regex(/^[a-zA-Z0-9_-]+(?:[/.][a-zA-Z0-9_-]+)*$/u)
  .refine(
    (s) =>
      !s.includes('..') && !/(?:^|\/)(?:answers?|answer-key|private|secrets?)(?:[/.]|$)/iu.test(s),
  );
const item = z
  .object({ id, label: z.string().min(1).max(1000), image: assetPath.optional() })
  .strict();
export const exerciseSchema = z
  .object({
    id,
    kind: z.enum(['classify', 'associate', 'order', 'choice']),
    title: z.string().min(1).max(200),
    prompt: z.string().min(1).max(6000),
    skills: z.array(id).max(20).default([]),
    items: z.array(item).min(2).max(40),
    targets: z.array(item).min(2).max(40).optional(),
  })
  .strict()
  .superRefine((e, c) => {
    if (new Set(e.items.map((i) => i.id)).size !== e.items.length)
      c.addIssue({ code: 'custom', message: 'Éléments dupliqués.' });
    if ((e.kind === 'classify' || e.kind === 'associate') && !e.targets)
      c.addIssue({ code: 'custom', message: 'Destinations manquantes.' });
    if (e.targets && new Set(e.targets.map((i) => i.id)).size !== e.targets.length)
      c.addIssue({ code: 'custom', message: 'Destinations dupliquées.' });
  });
export const manifestSchema = z
  .object({
    schemaVersion: z.literal(1),
    sdkVersion: version,
    id,
    version: z.string().regex(/^\d+\.\d+\.\d+$/u),
    title: z.string().min(1).max(200),
    intro: z.string().max(12000),
    conclusion: z.string().max(12000),
    objectives: z.array(z.string().max(1000)).max(20).default([]),
    skills: z
      .array(z.object({ id, label: z.string().min(1).max(300) }).strict())
      .max(50)
      .default([]),
    stages: z
      .array(
        z
          .object({
            id,
            title: z.string().min(1).max(200),
            exercises: z.array(exerciseSchema).min(1).max(40),
          })
          .strict(),
      )
      .min(1)
      .max(30),
  })
  .strict()
  .superRefine((m, c) => {
    const exercises = m.stages.flatMap((s) => s.exercises);
    if (
      exercises.length > 200 ||
      new Set(exercises.map((e) => e.id)).size !== exercises.length ||
      new Set(m.stages.map((s) => s.id)).size !== m.stages.length ||
      new Set(m.skills.map((s) => s.id)).size !== m.skills.length
    )
      c.addIssue({ code: 'custom', message: 'Identifiants dupliqués ou plus de 200 exercices.' });
    const skills = new Set(m.skills.map((s) => s.id));
    if (exercises.some((e) => e.skills.some((s) => !skills.has(s))))
      c.addIssue({ code: 'custom', message: 'Compétence inconnue.' });
  });
export const answerSchema = z.union([
  z.string().max(100),
  z.array(z.string().max(100)).max(40),
  z.record(id, z.string().max(100)),
]);
export const keySchema = z
  .object({
    answer: answerSchema,
    hint: z.string().min(1).max(3000),
    explanation: z.string().min(1).max(6000),
  })
  .strict();
export const bundleSchema = z
  .object({
    manifest: manifestSchema,
    answerKey: z.record(id, keySchema),
    files: z
      .array(
        z
          .object({
            path: assetPath,
            mime: z.enum([
              'text/html',
              'text/css',
              'text/javascript',
              'application/javascript',
              'application/json',
              'image/png',
              'image/jpeg',
              'image/webp',
              'image/svg+xml',
              'image/gif',
              'font/woff2',
            ]),
            base64: z
              .string()
              .max(MAX_BUNDLE_BYTES * 2)
              .regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/u),
          })
          .strict(),
      )
      .min(1)
      .max(150),
  })
  .strict();
export type MiniManifest = z.infer<typeof manifestSchema>;
export type Exercise = z.infer<typeof exerciseSchema>;
export type Answer = z.infer<typeof answerSchema>;
export type AnswerKey = Record<string, z.infer<typeof keySchema>>;
export type MiniBundle = z.infer<typeof bundleSchema>;
export function checkAnswer(exercise: Exercise, answer: Answer): void {
  const ids = exercise.items.map((i) => i.id);
  if (exercise.kind === 'choice') {
    if (typeof answer !== 'string' || !ids.includes(answer))
      throw new Error('Choisissez une réponse proposée.');
  } else if (exercise.kind === 'order') {
    if (
      !Array.isArray(answer) ||
      answer.length !== ids.length ||
      new Set(answer).size !== ids.length ||
      answer.some((i) => !ids.includes(i))
    )
      throw new Error('Placez chaque élément une seule fois.');
  } else {
    if (
      typeof answer !== 'object' ||
      Array.isArray(answer) ||
      Object.keys(answer).length !== ids.length ||
      ids.some(
        (i) => !Object.hasOwn(answer, i) || !exercise.targets?.some((t) => t.id === answer[i]),
      )
    )
      throw new Error('Attribuez une destination à chaque élément.');
    if (exercise.kind === 'associate' && new Set(Object.values(answer)).size !== ids.length)
      throw new Error('Associez chaque élément à une destination différente.');
  }
}
/** Même vocabulaire que la réponse, mais une correspondance peut être incomplète. */
export function checkDraft(exercise: Exercise, answer: Answer): void {
  if (exercise.kind === 'choice' && answer === '') return;
  if (exercise.kind === 'choice' || exercise.kind === 'order') {
    checkAnswer(exercise, answer);
    return;
  }
  if (
    typeof answer !== 'object' ||
    Array.isArray(answer) ||
    Object.entries(answer).some(
      ([item, target]) =>
        !exercise.items.some((i) => i.id === item) ||
        !exercise.targets?.some((t) => t.id === target),
    )
  )
    throw new Error('Le brouillon contient un élément ou une destination inconnue.');
  if (
    exercise.kind === 'associate' &&
    new Set(Object.values(answer)).size !== Object.keys(answer).length
  )
    throw new Error('Associez chaque élément à une destination différente.');
}
export function validateBundle(value: unknown): MiniBundle {
  const bundle = bundleSchema.parse(value);
  const exercises = bundle.manifest.stages.flatMap((s) => s.exercises);
  if (Object.keys(bundle.answerKey).length !== exercises.length)
    throw new Error('Un corrigé privé est nécessaire pour chaque exercice.');
  for (const ex of exercises) {
    const key = bundle.answerKey[ex.id];
    if (!key) throw new Error(`Corrigé absent : ${ex.id}`);
    checkAnswer(ex, key.answer);
  }
  const paths = new Set(bundle.files.map((f) => f.path));
  if (paths.size !== bundle.files.length || !paths.has('index.html'))
    throw new Error('Fichiers dupliqués ou index.html absent.');
  if (bundle.files.reduce((n, f) => n + (f.base64.length * 3) / 4, 0) > MAX_BUNDLE_BYTES)
    throw new Error('Le mini-site dépasse 8 Mo.');
  for (const ex of exercises)
    for (const i of [...ex.items, ...(ex.targets ?? [])])
      if (i.image && !paths.has(i.image)) throw new Error(`Image absente : ${i.image}`);
  return bundle;
}
export interface ExerciseResult {
  attempts: number;
  firstCorrect: boolean;
  correct: boolean;
  lastAnswer: Answer;
  feedback: string;
}
export interface MiniState {
  started: boolean;
  phase: 'introduction' | 'first-pass' | 'review' | 'completed';
  cursor: string | null;
  results: Record<string, ExerciseResult>;
  draft: { exerciseId: string; answer: Answer } | null;
  lastFeedback: string | null;
}
export interface MiniSnapshot {
  activityId: string;
  versionId: string;
  revision: number;
  manifest: MiniManifest;
  state: MiniState;
  unlockedStages: string[];
  activityStatus: string;
  pacing: 'self' | 'teacher';
  frameUrl: string | null;
  preview?: boolean;
}
export interface MiniSession {
  id: string;
  title: string;
  classId: string;
  className: string;
  status: string;
  pacing: 'self' | 'teacher';
  completed: boolean;
  correct: number;
  total: number;
}
export interface InteractiveCapability {
  list(): Promise<{ sessions: MiniSession[] }>;
  open(activityId: string): Promise<MiniSnapshot>;
  respond(
    activityId: string,
    revision: number,
    requestId: string,
    action: 'start' | 'draft' | 'submit',
    exerciseId?: string,
    answer?: Answer,
  ): Promise<MiniSnapshot>;
}
